import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface UserProfile {
  id: string;
  user_id: string;
  full_name: string;
  created_at: string;
  updated_at: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userProfile: UserProfile | null;
  userRoles: string[];
  loading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updatePassword: (password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = userRoles.includes('admin');

  const fetchUserProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
    
    if (!error && data) {
      setUserProfile(data);
    } else {
      setUserProfile(null);
    }
  };

  const fetchUserRoles = async (userId: string) => {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);
    
    if (!error && data) {
      setUserRoles(data.map(r => r.role));
    } else {
      setUserRoles([]);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('Auth state change:', event, session?.user?.email_confirmed_at);
        
        // Only set session and user if email is confirmed OR if this is a password reset
        if (session?.user) {
          if (session.user.email_confirmed_at || event === 'PASSWORD_RECOVERY') {
            setSession(session);
            setUser(session.user);
            
            // Fetch user profile and roles when user logs in with confirmed email
            setTimeout(() => {
              fetchUserProfile(session.user.id);
              fetchUserRoles(session.user.id);
            }, 0);
          } else {
            // Email not confirmed - force logout
            console.log('Email not confirmed, forcing logout');
            supabase.auth.signOut();
            setSession(null);
            setUser(null);
            setUserProfile(null);
            setUserRoles([]);
          }
        } else {
          setSession(null);
          setUser(null);
          setUserProfile(null);
          setUserRoles([]);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      console.log('Initial session check:', session?.user?.email_confirmed_at);
      
      if (session?.user) {
        if (session.user.email_confirmed_at) {
          setSession(session);
          setUser(session.user);
          fetchUserProfile(session.user.id);
          fetchUserRoles(session.user.id);
        } else {
          // Email not confirmed - sign out
          supabase.auth.signOut();
          setSession(null);
          setUser(null);
        }
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string, fullName?: string) => {
    const redirectUrl = `${window.location.origin}/auth?confirmed=true`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName
        }
      }
    });

    // Do NOT create profile immediately - wait for email confirmation
    // Profile will be created by the handle-email-confirmation edge function
    
    return { error };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth?reset=true`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (password: string) => {
    const { error } = await supabase.auth.updateUser({
      password: password
    });
    return { error };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      // Ignorar errores de sesión no encontrada y forzar limpieza local
      console.log('Error during signOut:', error);
    }
    
    // Forzar limpieza del estado local independientemente del resultado del servidor
    setSession(null);
    setUser(null);
    setUserProfile(null);
    setUserRoles([]);
  };

  return (
    <AuthContext.Provider value={{
      user,
      session,
      userProfile,
      userRoles,
      loading,
      isAdmin,
      signIn,
      signUp,
      resetPassword,
      updatePassword,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};